// A passkey on the device's own authenticator - fingerprint, face, or the
// screen lock - that the app asks for each time it comes back into view. It
// guards the screen, nothing more: no server checks the assertion, and the
// key stays remembered as before (./remembered.ts).
//
// In the Android app a passkey needs a provider app to keep it, which a phone
// may have none of; there the app asks for the device's lock itself, and the
// passkey is only a marker that one is set up.
import {
  fromBase64,
  toBase64,
} from './base64';

const TIMEOUT_MS = 60_000;
// authenticator data: a 32 byte rp id hash, then the flags; bit 2 is set once
// the device has checked who is holding it
const FLAGS_AT = 32;
const USER_VERIFIED = 0x04;

const ANDROID_ID = 'android';

const challenge = () => crypto.getRandomValues(new Uint8Array(32));

// the Android app, when it's one that asks for the lock itself
const android = () => {
  const app = window.notesAndroid;
  return app?.canVerify && app.verify
    ? { canVerify: app.canVerify.bind(app), verify: app.verify.bind(app) }
    : null;
};

// a cancelled prompt rejects as a cancelled passkey prompt does
const verifyInApp = (verify: () => void) =>
  new Promise<boolean>((resolve, reject) => {
    window.notesAndroidVerified = (error, cancelled) => {
      window.notesAndroidVerified = undefined;
      if (error === null) resolve(true);
      else if (cancelled) reject(new DOMException(error, 'NotAllowedError'));
      else reject(new Error(error));
    };
    verify();
  });

export const biometricsAvailable = async () => {
  const app = android();
  if (app) return app.canVerify();
  return typeof PublicKeyCredential !== 'undefined'
    && await PublicKeyCredential
      .isUserVerifyingPlatformAuthenticatorAvailable()
      .catch(() => false);
};

// The id of a new passkey for this repo. The user id is derived from the
// label, so setting one up again replaces the old one instead of piling up.
export const createPasskey = async (label: string) => {
  if (android()) return ANDROID_ID;
  const userId = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(label),
  );
  const credential = await navigator.credentials.create({
    publicKey: {
      challenge: challenge(),
      rp: { name: 'Notes' },
      user: { id: userId, name: label, displayName: label },
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 },
        { type: 'public-key', alg: -257 },
      ],
      authenticatorSelection: {
        authenticatorAttachment: 'platform',
        userVerification: 'required',
        residentKey: 'discouraged',
      },
      timeout: TIMEOUT_MS,
    },
  }) as PublicKeyCredential | null;
  if (!credential) throw new Error('no passkey was created');
  return toBase64(new Uint8Array(credential.rawId));
};

// true once the device has verified whoever is holding it; throws when the
// prompt is cancelled or the passkey is gone
export const verifyPasskey = async (id: string) => {
  const app = android();
  if (app) return verifyInApp(app.verify);
  const credential = await navigator.credentials.get({
    publicKey: {
      challenge: challenge(),
      allowCredentials: [{ type: 'public-key', id: fromBase64(id) }],
      userVerification: 'required',
      timeout: TIMEOUT_MS,
    },
  }) as PublicKeyCredential | null;
  const response = credential?.response as
    | AuthenticatorAssertionResponse
    | undefined;
  return !!response
    && (new Uint8Array(response.authenticatorData)[FLAGS_AT] & USER_VERIFIED)
      !== 0;
};
